import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";

const ROLE_OPTS = [
    { key: "RECEPTIONIST", label: "Receptionist" },
    { key: "HOUSEKEEPER", label: "Housekeeper" },
    { key: "SECURITY", label: "Security Guard" },
    { key: "OTHER", label: "Other" },
];

export default function AbsenceRulesPage() {
    const [sites, setSites] = useState([]);
    const [siteId, setSiteId] = useState(null);
    const [rules, setRules] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/sites");
            setSites(data);
            if (data.length && !siteId) setSiteId(data[0].id);
        })();
    }, [siteId]);

    useEffect(() => {
        if (!siteId) return;
        setLoading(true);
        (async () => {
            try {
                const { data } = await api.get(`/sites/${siteId}/absence-rules`);
                setRules(data);
            } catch (e) { toast.error(apiError(e)); }
            finally { setLoading(false); }
        })();
    }, [siteId]);

    function toggleRole(role) {
        setRules((r) => ({ ...r, required_roles: r.required_roles.includes(role) ? r.required_roles.filter((x) => x !== role) : [...r.required_roles, role] }));
    }

    async function save() {
        setSaving(true);
        try {
            const { data } = await api.put(`/sites/${siteId}/absence-rules`, {
                required_roles: rules.required_roles,
                client_inform_deadline_time: rules.client_inform_deadline_time,
                replacement_deadline_hours: Number(rules.replacement_deadline_hours),
            });
            setRules(data);
            toast.success("Rules saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    }

    return (
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">HO Admin</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">Absence Rules</h1>
                    <p className="text-sm text-gray-500 mt-1">Per-site contractual rules. Breaches auto-alert PM.</p>
                </div>
                <select value={siteId || ""} onChange={(e) => setSiteId(e.target.value)} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="absence-rules-site-select">
                    {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
            </div>

            {rules && !loading && (
                <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card p-5 space-y-5" data-testid="absence-rules-card">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Required roles (must be filled every working day)</label>
                        <div className="flex flex-wrap gap-2">
                            {ROLE_OPTS.map((r) => {
                                const active = rules.required_roles.includes(r.key);
                                return (
                                    <button
                                        key={r.key}
                                        onClick={() => toggleRole(r.key)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-[#1E3355] text-white border-[#1E3355]" : "bg-white text-gray-600 border-gray-200 hover:border-[#1E3355]"}`}
                                        data-testid={`role-toggle-${r.key}`}
                                    >{r.label}</button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Client inform deadline</label>
                            <input type="time" value={rules.client_inform_deadline_time} onChange={(e) => setRules({ ...rules, client_inform_deadline_time: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" data-testid="rules-deadline-time" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Replacement max (hours)</label>
                            <input type="number" step="0.5" min="0" value={rules.replacement_deadline_hours} onChange={(e) => setRules({ ...rules, replacement_deadline_hours: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" data-testid="rules-replacement-hours" />
                        </div>
                    </div>
                    <div className="pt-2 flex justify-end">
                        <button onClick={save} disabled={saving} className="bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60" data-testid="rules-save">
                            {saving ? "Saving…" : "Save rules"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
